/**
 * Dish artwork for the signup-page till.
 *
 * Drawn here, in SVG, rather than photographed. Three reasons, in order:
 *
 *   LICENSING. The demo photography the seeder pulls from Wikimedia is mostly
 *   CC BY-SA, which needs visible attribution wherever it appears — fine for a
 *   developer's seeded database, a wart on a public signup page. The seeder's
 *   own credits file makes a point of the repository carrying no third-party
 *   image licences, and this keeps that true.
 *
 *   WEIGHT. This is the one screen that must paint before anybody has an
 *   account. Six photographs is a few hundred kilobytes and six requests;
 *   this is a few kilobytes of markup that gzip likes, inside a bundle the
 *   page was already fetching.
 *
 *   SIZE. The tiles are 90–120px wide. A photograph of a curry at 110×82 is
 *   brown mush; flat shapes stay legible all the way down.
 *
 * Deliberately ONE style — flat, top-down, everything on a plate or in a cup,
 * the same palette and the same 21px plate throughout. Six illustrations that
 * do not agree with each other look worse than six emoji, and consistency is
 * most of what reads as "designed" at this size.
 *
 * The viewBox is 4:3 to match the tile, so the art fills it exactly and no
 * preserveAspectRatio letterboxing creeps in.
 */

export type Dish = 'chai' | 'samosa' | 'dosa' | 'vadapav' | 'coffee' | 'roll';

/* The shared palette. Warm and slightly desaturated — food photography sits in
   this range, and fully saturated flat colour reads as clip art. */
const PLATE = '#ffffff';
const PLATE_RIM = '#e7e1d6';
const FRIED = '#e0a33c';
const FRIED_DEEP = '#c8892a';
const BREAD = '#edd6a4';
const CHUTNEY = '#6fa84b';
const SAMBAR = '#d4703a';
const TEA = '#c2854a';
const COFFEE = '#8b5e3c';
const STEEL = '#d3cec6';

/** Backdrop per dish. Warm, low-contrast, distinct enough to tell tiles apart
    at a glance without turning the grid into a colour chart. */
const TINT: Record<Dish, [string, string]> = {
  chai: ['#f8f0e4', '#eee0cb'],
  samosa: ['#fcf3e0', '#f6e7ca'],
  dosa: ['#fdf7e8', '#f7ecd4'],
  vadapav: ['#fbf1de', '#f4e5c6'],
  coffee: ['#f2f2ef', '#e5e5e0'],
  roll: ['#faf3e6', '#f2e8d4'],
};

/** The plate everything sits on, so the six share a floor. */
function Plate() {
  return (
    <>
      <circle cx="40" cy="30" r="21" fill={PLATE} />
      <circle cx="40" cy="30" r="21" fill="none" stroke={PLATE_RIM} strokeWidth="1" />
      <circle cx="40" cy="30" r="16.5" fill="none" stroke={PLATE_RIM} strokeWidth="0.7" opacity="0.7" />
    </>
  );
}

/*
 * Everything below is sized to sit INSIDE the plate's inner ring (r=16.5
 * about 40,30). The first pass drew to the plate's outer edge and the dosa and
 * the pav visibly hung over the side, which at a glance reads as a rendering
 * bug rather than as a big portion.
 */
function Art({ dish }: { dish: Dish }) {
  switch (dish) {
    case 'chai':
      // A cup from above. The HANDLE is what does the work: concentric circles
      // alone read as a bowl of soup, and one small arc on the side turns the
      // same shape into a cup. The saucer is tinted rather than white so the
      // cup has something to sit on.
      return (
        <>
          <circle cx="40" cy="30" r="21" fill="#f7f2e9" />
          <circle cx="40" cy="30" r="21" fill="none" stroke={PLATE_RIM} strokeWidth="1" />
          <path
            d="M51.5 25.8 A 5.6 5.6 0 0 1 51.5 34.2"
            fill="none"
            stroke="#cdc2ae"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx="40" cy="30" r="13" fill={PLATE} stroke={PLATE_RIM} strokeWidth="0.9" />
          <circle cx="40" cy="30" r="9.5" fill={TEA} />
          {/* The skin of milk on chai left standing. */}
          <ellipse cx="37" cy="27.6" rx="4.2" ry="3" fill="#d9a271" opacity="0.85" />
          <circle cx="44" cy="32.5" r="1" fill="#6b8f4e" />
          <circle cx="41" cy="34.5" r="0.8" fill="#8a6a3f" />
        </>
      );

    case 'samosa':
      // The triangle, a fold, and a dot of chutney beside it.
      return (
        <>
          <Plate />
          <path
            d="M40 17 L53 37 L27 37 Z"
            fill={FRIED}
            stroke={FRIED_DEEP}
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <path d="M40 17 L40 37" stroke={FRIED_DEEP} strokeWidth="0.9" opacity="0.5" />
          <path d="M34 28 L46 28" stroke={FRIED_DEEP} strokeWidth="0.7" opacity="0.3" />
          <circle cx="40" cy="42" r="3.6" fill={CHUTNEY} />
          <circle cx="38.8" cy="41" r="1.2" fill="#87c063" opacity="0.7" />
        </>
      );

    case 'dosa':
      // Rolled, with sambar and coconut chutney below. A dosa served flat would
      // read as a pancake at this size; rolled is unmistakable.
      return (
        <>
          <Plate />
          <rect x="26" y="22" width="28" height="12" rx="6" fill={BREAD} stroke="#d9bd82" strokeWidth="1.1" />
          <path d="M30 24.2 Q40 22.6 50 24.2" stroke="#f6e9cd" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          <path d="M32 31.8 Q40 33.2 48 31.8" stroke="#d9bd82" strokeWidth="0.8" fill="none" opacity="0.55" />
          <circle cx="33" cy="41.5" r="4.2" fill={SAMBAR} />
          <circle cx="47" cy="41.5" r="4.2" fill="#f4f0e6" stroke={PLATE_RIM} strokeWidth="0.7" />
        </>
      );

    case 'vadapav':
      // The pav from above: a soft bun, seeded, with the vada showing at the
      // split across the middle.
      return (
        <>
          <Plate />
          <rect x="28" y="19" width="24" height="22" rx="8" fill="#dfae63" stroke="#c9954a" strokeWidth="1.2" />
          <path d="M28.6 30 Q40 27.8 51.4 30" stroke="#c9954a" strokeWidth="1" fill="none" />
          <path d="M30.5 30 Q40 32.4 49.5 30 Q40 34.2 30.5 30 Z" fill={FRIED_DEEP} opacity="0.85" />
          <ellipse cx="34.5" cy="23.5" rx="1.4" ry="0.95" fill="#f6e6c4" transform="rotate(-18 34.5 23.5)" />
          <ellipse cx="41.5" cy="22.2" rx="1.4" ry="0.95" fill="#f6e6c4" transform="rotate(10 41.5 22.2)" />
          <ellipse cx="46.5" cy="25" rx="1.4" ry="0.95" fill="#f6e6c4" transform="rotate(-8 46.5 25)" />
          <ellipse cx="37.5" cy="37.5" rx="1.4" ry="0.95" fill="#f6e6c4" transform="rotate(12 37.5 37.5)" />
        </>
      );

    case 'coffee':
      // Tumbler and dabarah, the steel pair filter coffee is served in — side
      // by side rather than nested. Nested, they were concentric rings that
      // read as a loudspeaker; apart, they read as two vessels.
      return (
        <>
          <Plate />
          <circle cx="34" cy="32.5" r="11.5" fill={STEEL} stroke="#bdb7ad" strokeWidth="0.9" />
          <circle cx="34" cy="32.5" r="8" fill="#e6e3dd" />
          <circle cx="50" cy="25.5" r="8" fill="#cbc5bc" stroke="#b5aea3" strokeWidth="0.9" />
          <circle cx="50" cy="25.5" r="5.6" fill={COFFEE} />
          <ellipse cx="48.6" cy="24.2" rx="2.2" ry="1.5" fill="#a97a52" opacity="0.8" />
        </>
      );

    case 'roll':
      // Wrapped, with the cut face showing the filling at one end.
      return (
        <>
          <Plate />
          <rect x="26" y="22" width="28" height="15" rx="7.5" fill={BREAD} stroke="#d9bd82" strokeWidth="1.1" />
          <path d="M34 22.4 L30 36.6" stroke="#d9bd82" strokeWidth="0.9" opacity="0.65" />
          <path d="M41 22.4 L37 36.6" stroke="#d9bd82" strokeWidth="0.9" opacity="0.65" />
          <ellipse cx="49.5" cy="29.5" rx="4.5" ry="7" fill="#faf6ec" stroke="#e3d5b4" strokeWidth="0.8" />
          <rect x="47.6" y="26.4" width="3.2" height="3.2" rx="0.7" fill="#f7f2e2" stroke="#ded2b6" strokeWidth="0.5" />
          <circle cx="50.8" cy="32.2" r="1.3" fill={CHUTNEY} opacity="0.85" />
          <circle cx="47.8" cy="32.8" r="1.1" fill="#d9603f" opacity="0.8" />
        </>
      );
  }
}

/**
 * One dish, filling a 4:3 box.
 *
 * The gradient ids are namespaced per dish because six of these render at
 * once and duplicate ids in one document resolve to whichever came first —
 * every tile would quietly wear the same backdrop.
 */
export function DishArt({ dish, className }: { dish: Dish; className?: string }) {
  const [from, to] = TINT[dish];

  return (
    <svg viewBox="0 0 80 60" className={className} role="presentation" aria-hidden focusable="false">
      <defs>
        <linearGradient id={`voltra-tile-${dish}`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0%" stopColor={from} />
          <stop offset="100%" stopColor={to} />
        </linearGradient>
        <radialGradient id={`voltra-tile-lit-${dish}`} cx="0.32" cy="0.2" r="0.75">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="80" height="60" fill={`url(#voltra-tile-${dish})`} />
      <rect width="80" height="60" fill={`url(#voltra-tile-lit-${dish})`} />

      {/* A soft contact shadow. Without it the plate floats and the whole set
          reads as stickers rather than as objects on a surface. */}
      <ellipse cx="40" cy="52" rx="19" ry="3.4" fill="#8a7a5f" opacity="0.16" />

      <Art dish={dish} />
    </svg>
  );
}
